import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

/**
 * The patient's food list, at the top of the Nutrition tab — where legacy
 * bridge's health plan shows it (new-bridge health-plan FoodList.js), in this
 * editor's design language (Nittin, 2026-09-17; it used to be a "Food list"
 * subtab in the left rail).
 *
 * Legacy's shape, kept:
 *   · a "Food list" head that opens and closes it — closed to begin with;
 *   · a search box, then five columns: the food TYPES ("All" first, then each
 *     type in legacy's order of first appearance — limited, not allowed,
 *     recommended, allowed), and Recommended / Allowed / Limited / Not allowed,
 *     each list scrolling on its own;
 *   · typing searches by name and puts the type back on "All"; picking a type
 *     narrows every column to it;
 *   · nothing at all while there is no food list (legacy hid it too).
 * One difference: a type picked after typing narrows the search results too
 * (legacy kept the type highlighted and ignored it).
 *
 * Display only — the list comes from /v1/foodlist and is rebuilt by Generate.
 * This file is identical in phr-frontend and bridge-frontend.
 */

const COLUMNS = [
  { key: "recommendedFoodList", label: "Recommended", tone: "good" },
  { key: "allowedFoodList", label: "Allowed", tone: "ok" },
  { key: "limitedFoodList", label: "Limited", tone: "warn" },
  { key: "notAllowedFoodList", label: "Not allowed", tone: "bad" },
];

// Legacy's order for collecting the types.
const TYPE_ORDER = ["limitedFoodList", "notAllowedFoodList", "recommendedFoodList", "allowedFoodList"];

const ALL = "All";

const rowsOf = (foodList, key) =>
  ((foodList && Array.isArray(foodList[key]) && foodList[key]) || []).filter(
    (r) => r && typeof r.foodItemName === "string" && r.foodItemName.trim()
  );

const typeOf = (r) => (typeof r.foodItemType === "string" ? r.foodItemType.trim() : "");

const Chev = ({ open }) => (
  <svg
    className={`hp-fl-chev${open ? " is-open" : ""}`}
    width="14"
    height="14"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

Chev.propTypes = { open: PropTypes.bool };

const HpFoodList = ({ foodList, status, open, onToggle, onRetry }) => {
  const [search, setSearch] = useState("");
  const [type, setType] = useState(ALL);

  const columns = useMemo(() => COLUMNS.map((c) => ({ ...c, rows: rowsOf(foodList, c.key) })), [foodList]);

  const types = useMemo(() => {
    const seen = [];
    TYPE_ORDER.forEach((k) =>
      rowsOf(foodList, k).forEach((r) => {
        const t = typeOf(r);
        if (t && t !== ALL && seen.indexOf(t) === -1) seen.push(t);
      })
    );
    return [ALL, ...seen];
  }, [foodList]);

  const total = columns.reduce((n, c) => n + c.rows.length, 0);

  if (status === "error") {
    return (
      <div className="hp-card hp-fl is-error" role="alert">
        <span className="hp-fl-title">Food list</span>
        <span className="hp-fl-error">Couldn&rsquo;t load the food list.</span>
        <button type="button" className="hp-add-link hp-fl-retry" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }
  if (status !== "loaded" || total === 0) return null;

  const q = search.trim().toLowerCase();
  const shown = columns.map((c) => ({
    ...c,
    visible: c.rows.filter(
      (r) => (type === ALL || typeOf(r) === type) && (!q || r.foodItemName.toLowerCase().includes(q))
    ),
  }));

  return (
    <section className={`hp-card hp-fl${open ? " is-open" : ""}`} aria-label="Food list">
      <button type="button" className="hp-fl-head hp-fl-toggle" aria-expanded={open} onClick={onToggle}>
        <span className="hp-fl-title">Food list</span>
        <span className="hp-fl-summary">
          {columns.map((c) => (
            <span key={c.key} className={`hp-fl-count is-${c.tone}`}>
              {c.rows.length} {c.label.toLowerCase()}
            </span>
          ))}
        </span>
        <Chev open={open} />
      </button>

      {open && (
        <div className="hp-fl-body">
          <div className="hp-fl-search">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              className="hp-fl-search-input"
              type="search"
              value={search}
              placeholder="Search food"
              aria-label="Search the food list"
              onChange={(e) => {
                setSearch(e.target.value);
                setType(ALL);
              }}
            />
          </div>

          <div className="hp-fl-grid">
            <div className="hp-fl-col is-types">
              <div className="hp-fl-col-head">
                <span className="hp-fl-col-label">Type</span>
              </div>
              <div className="hp-fl-list" role="group" aria-label="Food type">
                {types.map((t) => (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={type === t}
                    className={`hp-fl-type hp-fl-type-btn${type === t ? " is-active" : ""}`}
                    onClick={() => setType(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {shown.map((c) => (
              <div className="hp-fl-col" key={c.key} data-list={c.key}>
                <div className="hp-fl-col-head">
                  <span className={`hp-food-tag is-${c.tone}`}>{c.label}</span>
                  <span className="hp-fl-col-count">{c.visible.length}</span>
                </div>
                {/* Focusable, so the list can be scrolled from the keyboard. */}
                <ul className="hp-fl-list" tabIndex={0} aria-label={`${c.label} foods`}>
                  {c.visible.length === 0 ? (
                    <li className="hp-fl-none">{q || type !== ALL ? "No match" : "None"}</li>
                  ) : (
                    c.visible.map((r, i) => (
                      <li
                        key={`${r.foodItemId || r.foodItemName}-${i}`}
                        className="hp-fl-item"
                        title={typeOf(r) || undefined}
                      >
                        {r.foodItemName}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

HpFoodList.propTypes = {
  foodList: PropTypes.object,
  status: PropTypes.oneOf(["idle", "loading", "loaded", "error"]).isRequired,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
};

export default HpFoodList;
